// The immutable half of the currency policy matrix, as the operator
// console needs to render it (SM-396 README → "Currency policy matrix").
//
// Everything in this file is a CEILING or a ROLE: facts that no
// operator, request, migration or flag can change. They are duplicated
// here — rather than read from the registry — on purpose:
//
//   - the page must be able to explain WHY a switch is locked even when
//     the registry read is unavailable (F1 dark, upstream down), and a
//     locked control with no explanation is exactly the "presented as
//     merely off" failure the phase forbids;
//   - a ceiling that arrived over the wire could, in principle, arrive
//     wrong. These are constants in the epic's acceptance criteria, so
//     the console asserts them rather than trusting a payload, and
//     `ceilingDisagreement()` below turns a mismatch into something an
//     operator can see instead of something that silently widens a rail.
//
// The ledger remains the enforcement authority. Nothing here grants a
// capability; it only decides what the screen says.

import type { CurrencyCode } from "@/lib/currency";

export type Direction = "deposit" | "withdraw";

export interface CurrencyPolicy {
  /** What this currency is FOR, in one line. */
  role: string;
  /** Immutable capability ceilings — what may EVER be enabled. */
  ceiling: Record<Direction, boolean>;
  /** Why a false ceiling is false. Rendered beside the locked control. */
  lockReason: Partial<Record<Direction, string>>;
  /** Conversion routes OUT of this currency. Exactly these, forever. */
  convertTo: CurrencyCode[];
  /** Conversion routes INTO this currency. */
  convertFrom: CurrencyCode[];
  /** Where the currency is debited / credited in trading. */
  trading: string;
}

const POLICY: Record<CurrencyCode, CurrencyPolicy> = {
  RED: {
    role: "GAME buy-in",
    ceiling: { deposit: true, withdraw: false },
    lockReason: {
      withdraw:
        "RED can never be withdrawn. It is the GAME buy-in currency: value leaves GAME as GREEN, which converts to GOLD and withdraws there.",
    },
    convertTo: [],
    convertFrom: ["GREEN"],
    trading: "Debited on a GAME BUY.",
  },
  GREEN: {
    role: "GAME winnings + promotional rewards",
    ceiling: { deposit: false, withdraw: false },
    lockReason: {
      deposit:
        "GREEN can never be deposited. It is earned — from a GAME sell, redeem or resolution payout, or from a campaign reward — never bought.",
      withdraw:
        "GREEN can never be withdrawn directly. Convert it to GOLD first; the GOLD rail is the only way value leaves the platform.",
    },
    convertTo: ["RED", "GOLD"],
    convertFrom: [],
    trading: "Credited on a GAME sell, redeem or resolution payout.",
  },
  GOLD: {
    role: "CASH trading (the legacy money)",
    ceiling: { deposit: true, withdraw: true },
    lockReason: {},
    convertTo: [],
    convertFrom: ["GREEN"],
    trading: "Debited on a CASH BUY, credited on a CASH sell, redeem or resolution payout.",
  },
};

export const CURRENCY_POLICY: Readonly<Record<CurrencyCode, CurrencyPolicy>> = Object.freeze(POLICY);

/** Display order — RED, GREEN, GOLD, matching the README's matrix. */
export const POLICY_ORDER: readonly CurrencyCode[] = ["RED", "GREEN", "GOLD"] as const;

/**
 * A registry row whose ceiling contradicts the epic's fixed matrix.
 *
 * This should never fire. If it does, either the ledger seed drifted or
 * something is answering for the registry that should not be — both are
 * operator-visible incidents, not things to paper over by rendering
 * whichever value happens to be more permissive. Returns the directions
 * that disagree, or an empty array.
 */
export function ceilingDisagreement(
  code: CurrencyCode,
  wire: { deposit_ceiling: boolean; withdraw_ceiling: boolean },
): Direction[] {
  const policy = CURRENCY_POLICY[code];
  const out: Direction[] = [];
  if (wire.deposit_ceiling !== policy.ceiling.deposit) out.push("deposit");
  if (wire.withdraw_ceiling !== policy.ceiling.withdraw) out.push("withdraw");
  return out;
}

/** Human label for a conversion route id (`GREEN_TO_GOLD` → `GREEN → GOLD`). */
export function routeLabel(route: string): string {
  const [from, , to] = route.split("_");
  return from && to ? `${from} → ${to}` : route;
}
